import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { authAPI, businessAPI, accountingAPI, contactAPI } from '../services/api';
import { getApiErrorMessage } from '../utils/authValidation';

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  status: string;
  is_super_admin?: boolean;
  created_at: string;
  created_by_first_name?: string;
  created_by_last_name?: string;
  role: string;
  is_active: boolean;
}

interface Business {
  id: number;
  name: string;
  email: string;
  phone: string;
  address: string;
  country_code: string;
  base_currency_code: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface Account {
  id: number;
  name: string;
  type: string;
  code: string;
}

interface Contact {
  id: number;
  company_name: string;
  contact_name: string;
  email: string;
  phone: string;
  contact_type: string;
}

interface HybridAuthContextType {
  user: User | null;
  business: Business | null;
  accounts: Account[];
  contacts: Contact[];
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isLoggingOut: boolean;
  login: (email: string, password: string, businessId?: string) => Promise<void>;
  register: (userData: any, businessData?: any) => Promise<void>;
  logout: () => void;
  refreshData: (force?: boolean) => Promise<void>;
  refreshSharedData: () => Promise<void>;
  switchBusiness: (businessId: string) => Promise<void>;
  createBusiness: (businessData: any) => Promise<void>;
}

const HybridAuthContext = createContext<HybridAuthContextType | undefined>(undefined);

export const useHybridAuth = () => {
  const context = useContext(HybridAuthContext);
  if (context === undefined) {
    throw new Error('useHybridAuth must be used within a HybridAuthProvider');
  }
  return context;
};

const MIN_AUTH_REFRESH_MS = 8000;

export const HybridAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const fetchInFlightRef = useRef<Promise<void> | null>(null);
  const lastFetchAtRef = useRef(0);

  const loadAccountsAndContacts = useCallback(async (businessId: number | null) => {
    try {
      const [accountsResponse, contactsResponse] = await Promise.all([
        businessId
          ? accountingAPI.getAccounts({ business_id: businessId })
          : accountingAPI.getAccounts(),
        businessId
          ? contactAPI.getAll(businessId)
          : Promise.resolve({ data: { success: true, data: { contacts: [] } } }),
      ]);

      if (accountsResponse.data.success) {
        const rawAccounts = accountsResponse.data.data.accounts || [];
        setAccounts(rawAccounts.map((account: any) => ({
          id: account.id,
          name: account.account_name || account.name,
          type: account.account_type || account.type,
          code: account.account_code || account.code,
        })));
      }

      if (contactsResponse.data.success) {
        const rawContacts = contactsResponse.data.data.contacts || [];
        setContacts(rawContacts.map((contact: any) => ({
          id: contact.id,
          company_name: contact.company_name || '',
          contact_name: contact.contact_name || '',
          email: contact.email || '',
          phone: contact.phone || '',
          contact_type: contact.contact_type || '',
        })));
      }
    } catch (dataError) {
      console.warn('Could not fetch shared data:', dataError);
    }
  }, []);

  const fetchUserData = useCallback(async (options?: { force?: boolean }) => {
    const force = options?.force === true;
    const now = Date.now();
    if (!force && now - lastFetchAtRef.current < MIN_AUTH_REFRESH_MS) {
      return;
    }

    if (fetchInFlightRef.current) {
      return fetchInFlightRef.current;
    }

    const run = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          return;
        }

        const userResponse = await authAPI.getMe();
        if (userResponse.data.success) {
          const userData = userResponse.data.data.user;
          setUser(userData);
          setIsAuthenticated(true);
          lastFetchAtRef.current = Date.now();

          let businessId: number | null = null;
          try {
            const userBusinessesResponse = await businessAPI.getUserBusinesses();
            if (userBusinessesResponse.data.success && userBusinessesResponse.data.data.businesses.length > 0) {
              const businesses = userBusinessesResponse.data.data.businesses;
              const currentBusiness = businesses.find((b: any) => b.email === userData.email)
                || businesses.find((b: any) => b.role === 'administrator')
                || businesses[0];
              setBusiness(currentBusiness);
              businessId = currentBusiness.id;
              localStorage.setItem('businessId', String(currentBusiness.id));
            }
          } catch (businessError) {
            console.warn('Could not fetch user businesses:', businessError);
          }

          await loadAccountsAndContacts(businessId);
        } else {
          throw new Error(userResponse.data.error || 'Failed to fetch user data');
        }
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 429) {
          console.warn('Auth refresh rate-limited; keeping current session.');
          setError('Too many requests. Please wait a moment before refreshing.');
          return;
        }

        console.error('Error fetching data:', err);
        if (status === 401) {
          setIsAuthenticated(false);
          setUser(null);
          setBusiness(null);
          setAccounts([]);
          setContacts([]);
        }
        setError(err.message || 'Failed to fetch user data');
      } finally {
        setLoading(false);
        fetchInFlightRef.current = null;
      }
    };

    fetchInFlightRef.current = run();
    return fetchInFlightRef.current;
  }, [loadAccountsAndContacts]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      void fetchUserData({ force: true });
    }
  }, [fetchUserData]);

  const login = async (email: string, password: string, businessId?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const { data } = await authAPI.login(email, password);
      localStorage.setItem('token', data.data.token);
      await fetchUserData({ force: true });
    } catch (err: any) {
      const message = err?.response?.data?.error || (err instanceof Error ? err.message : 'Login failed');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: any, businessData?: any) => {
    try {
      setLoading(true);
      setError(null);

      // Create user first
      const userResponse = await authAPI.register({
        email: userData.email,
        password: userData.password,
        first_name: userData.first_name,
        last_name: userData.last_name,
        role: 'admin' // First user becomes admin of their business
      });

      if (!userResponse.data.success) {
        throw new Error(userResponse.data.error || 'Failed to create user');
      }

      // Store token
      localStorage.setItem('token', userResponse.data.data.token);

      // Create business if provided
      if (businessData) {
        const businessResponse = await businessAPI.create(businessData);
        
        if (!businessResponse.data.success) {
          throw new Error(businessResponse.data.error || 'Failed to create business');
        }

        const newBusiness = businessResponse.data.data.business;
        setBusiness(newBusiness);
      }

      // Refresh user data
      await fetchUserData({ force: true });
    } catch (err: unknown) {
      const message = getApiErrorMessage(err, 'Registration failed');
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setIsLoggingOut(true);
    localStorage.setItem('isLoggingOut', 'true');
    localStorage.removeItem('token');
    localStorage.removeItem('businessId');
    setUser(null);
    setBusiness(null);
    setAccounts([]);
    setContacts([]);
    setIsAuthenticated(false);
    setError(null);

    // Reset logging out flag after a short delay to allow state changes to complete
    setTimeout(() => {
      setIsLoggingOut(false);
      localStorage.removeItem('isLoggingOut');
    }, 500);
  };

  const refreshData = async (force?: boolean) => {
    await fetchUserData({ force });
  };

  const refreshSharedData = async () => {
    const storedId = parseInt(localStorage.getItem('businessId') || '', 10);
    const businessId = business?.id ?? (Number.isFinite(storedId) ? storedId : null);
    await loadAccountsAndContacts(businessId);
  };

  const switchBusiness = async (businessId: string) => {
    try {
      setLoading(true);
      
      // Fetch business data
      const businessResponse = await businessAPI.getById(parseInt(businessId));
      if (businessResponse.data.success) {
        setBusiness(businessResponse.data.data.business);
        localStorage.setItem('businessId', businessId);
        
        // Fetch contacts for this business
        const contactsResponse = await contactAPI.getAll(parseInt(businessId));
        if (contactsResponse.data.success) {
          const rawContacts = contactsResponse.data.data.contacts || [];
          const mappedContacts = rawContacts.map((contact: any) => ({
            id: contact.id,
            company_name: contact.company_name || '',
            contact_name: contact.contact_name || '',
            email: contact.email || '',
            phone: contact.phone || '',
            contact_type: contact.contact_type || ''
          }));
          setContacts(mappedContacts);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to switch business');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createBusiness = async (businessData: any) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await businessAPI.create(businessData);
      
      if (response.data.success) {
        const newBusiness = response.data.data.business;
        setBusiness(newBusiness);
        // Switch to the new business
        await switchBusiness(newBusiness.id.toString());
      } else {
        throw new Error(response.data.error || 'Failed to create business');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create business');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const contextValue: HybridAuthContextType = {
    user,
    business,
    accounts,
    contacts,
    loading,
    error,
    isAuthenticated,
    isLoggingOut,
    login,
    register,
    logout,
    refreshData,
    refreshSharedData,
    switchBusiness,
    createBusiness,
  };

  return (
    <HybridAuthContext.Provider value={contextValue}>
      {children}
    </HybridAuthContext.Provider>
  );
};

export default HybridAuthContext;
